/**
 * Casella di ricerca. Controllata: il debounce e la query stanno nella pagina,
 * qui c'è solo il controllo.
 *
 * L'ambito («tutta l'area» o «solo questa cartella») è un interruttore visibile
 * e non un'opzione nascosta: cercare in tutto l'archivio e cercare nella
 * cartella aperta danno risultati molto diversi, e l'utente deve sapere quale
 * dei due sta guardando.
 */
import { useRef } from 'react';
import './SearchBox.css';

export function SearchBox({
  value,
  onChange,
  scope,
  onScopeChange,
  folderName,
  busy,
}: {
  value: string;
  onChange: (value: string) => void;
  scope: 'area' | 'folder';
  onScopeChange: (scope: 'area' | 'folder') => void;
  /** Nome della cartella aperta. `null` quando si è già alla radice. */
  folderName: string | null;
  busy: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className="searchbox">
      <div className="searchbox__field">
        <IconSearch />
        <input
          ref={input}
          className="searchbox__input"
          type="search"
          value={value}
          placeholder="Cerca documenti…"
          aria-label="Cerca documenti"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          // Esc svuota: è il gesto che tutti si aspettano da un campo di ricerca.
          onKeyDown={(e) => {
            if (e.key === 'Escape' && value !== '') {
              e.preventDefault();
              onChange('');
            }
          }}
        />
        {busy ? <span className="searchbox__spinner" aria-hidden="true" /> : null}
        {value ? (
          <button
            type="button"
            className="searchbox__clear"
            onClick={() => {
              onChange('');
              input.current?.focus();
            }}
          >
            <span className="visually-hidden">Annulla la ricerca</span>
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
      </div>

      {/* Alla radice l'alternativa non esiste: i due ambiti coinciderebbero. */}
      {folderName !== null ? (
        <div className="searchbox__scope" role="group" aria-label="Ambito della ricerca">
          <button
            type="button"
            aria-pressed={scope === 'area'}
            className={scope === 'area' ? 'is-active' : undefined}
            onClick={() => onScopeChange('area')}
          >
            Tutta l'area
          </button>
          <button
            type="button"
            aria-pressed={scope === 'folder'}
            className={scope === 'folder' ? 'is-active' : undefined}
            onClick={() => onScopeChange('folder')}
            title={`Cerca solo in ${folderName}`}
          >
            Questa cartella
          </button>
        </div>
      ) : null}
    </div>
  );
}

function IconSearch() {
  return (
    <svg
      className="searchbox__icon"
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
